import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { getMyCapabilities } from "../services/capabilityService";
import { hasMenuAccess, normalizeMenuPath } from "../services/menuService";

const CapabilitiesContext = createContext(null);

const PERMISSION_RESOURCE_ALIASES = {
  masters: ["masters", "reference"],
  reference: ["reference", "masters"],
};

const toArray = (value) => (Array.isArray(value) ? value : []);
const toObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? value : {};

// Normalize capability menu items into the shape menuService.hasMenuAccess expects.
// Capabilities API only returns items the user can access, so all are treated as active.
const normalizeCapabilityMenu = (items) =>
  toArray(items).map((item) => ({
    ...item,
    id: item?.id ?? item?.key ?? item?.path ?? String(Math.random()),
    key: item?.key ?? "",
    label: String(item?.label ?? "").trim(),
    path: normalizeMenuPath(item?.path, item?.key, item?.label),
    icon: item?.icon ?? "",
    parent_id: item?.parent_id ?? null,
    sort_order: Number(item?.sort_order ?? 0),
    is_active: true,
  }));

const normalizeCapabilitiesPayload = (data) => ({
  user: data?.user ?? null,
  menu: normalizeCapabilityMenu(data?.menu),
  permissions: toObject(data?.permissions),
  assignableRoles: toArray(data?.assignable_roles),
  tables: toObject(data?.tables),
});

const getPermissionResourceKeys = (resource) => {
  const requestedResources = Array.isArray(resource) ? resource : [resource];

  return Array.from(
    new Set(
      requestedResources
        .flatMap((resourceKey) => {
          const normalizedResourceKey = String(resourceKey ?? "").trim();

          if (!normalizedResourceKey) {
            return [];
          }

          return [
            normalizedResourceKey,
            ...(PERMISSION_RESOURCE_ALIASES[normalizedResourceKey] || []),
          ];
        })
        .filter(Boolean)
    )
  );
};

export function CapabilitiesProvider({ children, sessionKey = "" }) {
  const navigate = useNavigate();

  // null = not yet fetched; object = fetched (may have empty fields on error)
  const [capabilities, setCapabilities] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const clearCapabilities = useCallback(() => {
    setCapabilities(null);
    setError("");
    setLoading(false);
  }, []);

  const loadCapabilities = useCallback(async () => {
    const authKey = localStorage.getItem("authKey");

    if (!authKey) {
      clearCapabilities();
      return null;
    }

    setLoading(true);
    setError("");

    try {
      const data = await getMyCapabilities();
      const normalized = normalizeCapabilitiesPayload(data);
      setCapabilities(normalized);
      return normalized;
    } catch (err) {
      const statusCode = err?.response?.status;

      if (statusCode === 401) {
        clearCapabilities();
        localStorage.removeItem("authKey");
        localStorage.removeItem("userName");
        navigate("/login", { replace: true });
      } else {
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Unable to load capabilities."
        );
      }

      return null;
    } finally {
      setLoading(false);
    }
  }, [clearCapabilities, navigate]);

  const refreshCapabilities = useCallback(
    () => loadCapabilities(),
    [loadCapabilities]
  );

  useEffect(() => {
    if (sessionKey) {
      loadCapabilities();
    } else {
      clearCapabilities();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionKey]);

  // Returns true while capabilities are not yet fetched to avoid UI flash.
  const can = useCallback(
    (resource, action) => {
      if (!capabilities) {
        return true;
      }

      return getPermissionResourceKeys(resource).some((resourceKey) => {
        const actions = capabilities.permissions[resourceKey];
        return Array.isArray(actions) && actions.includes(action);
      });
    },
    [capabilities]
  );

  const canAny = useCallback(
    (resource, actionsToCheck) => {
      if (!capabilities) {
        return true;
      }

      const requestedActions = Array.isArray(actionsToCheck)
        ? actionsToCheck
        : [actionsToCheck];

      return getPermissionResourceKeys(resource).some((resourceKey) => {
        const allowedActions = capabilities.permissions[resourceKey];

        return (
          Array.isArray(allowedActions) &&
          requestedActions.some((action) => allowedActions.includes(action))
        );
      });
    },
    [capabilities]
  );

  const safeMenu = useMemo(
    () => toArray(capabilities?.menu),
    [capabilities]
  );

  const checkHasMenuAccess = useCallback(
    (path) => {
      if (!capabilities) {
        return true;
      }

      return hasMenuAccess(safeMenu, path);
    },
    [capabilities, safeMenu]
  );

  const getTableConfig = useCallback(
    (tableName) => toObject(toObject(capabilities?.tables)[tableName]),
    [capabilities]
  );

  const value = useMemo(
    () => ({
      capabilities,
      loading,
      error,
      user: capabilities?.user ?? null,
      menu: safeMenu,
      permissions: toObject(capabilities?.permissions),
      assignableRoles: toArray(capabilities?.assignableRoles),
      tables: toObject(capabilities?.tables),
      can,
      canAny,
      hasMenuAccess: checkHasMenuAccess,
      getTableConfig,
      loadCapabilities,
      refreshCapabilities,
      clearCapabilities,
    }),
    [
      capabilities,
      loading,
      error,
      safeMenu,
      can,
      canAny,
      checkHasMenuAccess,
      getTableConfig,
      loadCapabilities,
      refreshCapabilities,
      clearCapabilities,
    ]
  );

  return (
    <CapabilitiesContext.Provider value={value}>
      {children}
    </CapabilitiesContext.Provider>
  );
}

export function useCapabilities() {
  const context = useContext(CapabilitiesContext);

  if (!context) {
    throw new Error(
      "useCapabilities must be used within a CapabilitiesProvider."
    );
  }

  return context;
}
